<template>
  <div id="pageVehiculoDetails">
    <!-- Barra superior con acciones y navegación -->
    <v-card class="mb-4 elevation-2">
      <v-app-bar flat dense color="primary" dark>
        <v-btn icon @click="$router.go(-1)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title>Ficha de Vehículo</v-toolbar-title>
        <v-spacer></v-spacer>

        <!-- Acción Crear Movimiento - Permanece en la barra superior -->
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="addMovimiento" color="white" class="ml-1" v-if="!loading">
              <v-icon>mdi-car-arrow-right</v-icon>
            </v-btn>
          </template>
          <span>Crear movimiento</span>
        </v-tooltip>

        <!-- Acción Imprimir - Permanece en la barra superior -->
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" :to="{ path: `/printVehiculo/${car ? car.id : ''}` }" color="white" class="ml-1"
              v-if="!loading">
              <v-icon>mdi-printer</v-icon>
            </v-btn>
          </template>
          <span>Imprimir ficha</span>
        </v-tooltip>
        <!-- Editar/Eliminar en móvil: mostrar en app-bar cuando no es mdAndUp -->
        <template v-if="$vuetify && $vuetify.breakpoint && !$vuetify.breakpoint.mdAndUp && !loading && car">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on" color="white" class="ml-1" @click="showVehiculoForm(car)" v-if="canEditVehiculo">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>Editar vehículo</span>
          </v-tooltip>

          <!-- <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on" color="white" class="ml-1" @click="confirmDeleteVehiculo" v-if="canDeleteVehiculo">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>Eliminar vehículo</span>
          </v-tooltip> -->
        </template>
      </v-app-bar>
    </v-card>

    <!-- Estado de carga -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="50" width="5"></v-progress-circular>
      <div class="mt-4 text-subtitle-1 font-weight-medium grey--text text--darken-1">
        Cargando detalles del vehículo...
      </div>
    </div>

    <template v-else>
      <v-container fluid>
        <!-- Panel principal con información y ubicación -->
        <v-row>
          <!-- Información del vehículo -->
          <v-col cols="12" lg="5">
            <v-card class="mb-4 elevation-1">
              <!-- Header con información principal -->
              <div class="vehicle-header pa-4">
                <div class="d-flex align-center">
                  <v-avatar color="primary" size="54" class="mr-4">
                    <v-icon size="32" dark>{{ getCarIcon(car.condition) }}</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-h5 font-weight-bold">{{ car.brand }} {{ car.model }}</div>
                    <div class="subtitle-1 grey--text text--darken-1">
                      {{ car.version }} • {{ car.color }}
                       <span v-if="car.plate_number"> • {{ car.plate_number }}</span>
                      <span v-else> • Sin matrícula</span>
                    </div>
                  </div>
                  <v-spacer></v-spacer>

                  <!-- Acciones de edición - Solo iconos junto a la información principal -->
                  <div v-if="$vuetify && $vuetify.breakpoint && $vuetify.breakpoint.mdAndUp">
                    <v-btn icon color="primary" @click="showVehiculoForm(car)" v-if="canEditVehiculo">
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </div>
                </div>

                <!-- Segunda fila de info - Estado y badges -->
                <div class="d-flex mt-4 flex-wrap justify-space-between">
                  <!-- Badges informativos -->
                  <div class="d-flex flex-wrap flex-grow-1">
                    <div class="info-badge mr-2 mb-2">
                      <v-icon x-small left color="blue-grey darken-1">mdi-engine</v-icon>
                      {{ car.engine || 'N/A' }}
                    </div>
                    <div class="info-badge mr-2 mb-2">
                      <v-icon x-small left color="blue-grey darken-1">mdi-gas-station</v-icon>
                      {{ car.fuel || 'N/A' }}
                    </div>
                    <div class="info-badge mr-2 mb-2">
                      <v-icon x-small left color="blue-grey darken-1">mdi-car-shift-pattern</v-icon>
                      {{ car.transmission || 'N/A' }}
                    </div>
                    <div class="info-badge mr-2 mb-2">
                      <v-icon x-small left color="blue-grey darken-1">mdi-store</v-icon>
                      {{ car.dealer.name }}
                    </div>
                    <div class="info-badge mr-2 mb-2">
                      <v-icon x-small left color="blue-grey darken-1">mdi-speedometer</v-icon>
                      {{ car.current_kms != null ? car.current_kms + ' km' : 'N/A' }}
                    </div>
                    <div class="info-badge mb-2">
                      <v-icon x-small left color="blue-grey darken-1">mdi-map-marker</v-icon>
                      {{ car.location ? car.location.name : 'Sin ubicación' }}
                    </div>
                  </div>

                  <!-- Chip de estado -->
                  <v-chip :color="car.condition === 'new' ? 'success' : 'blue-grey'" small class="ml-auto" dark>
                    <v-icon left x-small>
                      {{ car.condition === 'new' ? 'mdi-new-box' : 'mdi-recycle' }}
                    </v-icon>
                    {{ car.condition === 'new' ? 'Nuevo' : car.condition === 'taller' ? 'Taller' : 'Usado' }}
                  </v-chip>
                </div>
              </div>

              <v-divider></v-divider>

              <!-- Botones de acción - Entre datos principales e información adicional -->
              <v-card-text class="py-3">
                <div class="d-flex flex-wrap justify-center align-center">
                  <v-btn
                    outlined
                    color="primary"
                    class="ma-1"
                    @click="openTransportForm(car)"
                    v-if="canMov(car)"
                  >
                    <v-icon left>mdi-car-arrow-right</v-icon>
                    Petición de movimiento
                  </v-btn>

                  <v-btn
                    outlined
                    color="primary"
                    class="ma-1"
                    @click="openTransportFormRecepcionar(car)"
                    v-if="canRecepcionar(car)"
                  >
                    <v-icon left>mdi-car-arrow-right</v-icon>
                    Recepcionar
                  </v-btn>

                  <v-btn
                    outlined
                    color="primary"
                    class="ma-1"
                    @click="openAssignmentForm(car)"
                    v-if="canCesion(car)"
                  >
                    <v-icon left>mdi-account-arrow-right</v-icon>
                    Crear cesión
                  </v-btn>

                  <v-btn
                    outlined
                    color="warning"
                    class="ma-1"
                    @click="confirmFreeVehicle"
                    v-if="canFreeVehicle(car)"
                  >
                    <v-icon left>mdi-lock-open</v-icon>
                    Archivar vehículo
                  </v-btn>
                </div>
              </v-card-text>

              <v-divider></v-divider>

              <!-- Pestañas de información -->
              <v-tabs v-model="detailsTab" background-color="transparent" grow class="detail-tabs">
                <v-tab>
                  <v-icon :left="$vuetify.breakpoint.mdAndUp">mdi-information-outline</v-icon>
                  <span v-if="$vuetify.breakpoint.mdAndUp">Información básica</span>
                </v-tab>
                <v-tab>
                  <v-icon :left="$vuetify.breakpoint.mdAndUp">mdi-file-document-outline</v-icon>
                  <span v-if="$vuetify.breakpoint.mdAndUp">Documentación</span>
                </v-tab>
              </v-tabs>

              <v-divider></v-divider>

              <v-tabs-items v-model="detailsTab">
                <!-- Pestaña de información básica (sin cambios) -->
                <v-tab-item>
                  <v-card flat>
                    <v-card-text>
                      <v-row>
                        <!-- Columna izquierda -->
                        <v-col cols="12" md="6">
                          <v-list dense class="transparent">
                            <v-list-item class="px-0">
                              <v-list-item-icon class="mr-2">
                                <v-icon>mdi-tag-outline</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="caption grey--text">Marca</v-list-item-title>
                                <v-list-item-subtitle class="subtitle-1">{{ car.brand }}</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>

                            <v-list-item class="px-0">
                              <v-list-item-icon class="mr-2">
                                <v-icon>mdi-car-info</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="caption grey--text">Modelo</v-list-item-title>
                                <v-list-item-subtitle class="subtitle-1">{{ car.model }}</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>

                            <v-list-item class="px-0">
                              <v-list-item-icon class="mr-2">
                                <v-icon>mdi-car-back</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="caption grey--text">Versión</v-list-item-title>
                                <v-list-item-subtitle class="subtitle-1">{{ car.version || 'No especificada'
                                  }}</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>

                            <v-list-item class="px-0">
                              <v-list-item-icon class="mr-2">
                                <v-icon>mdi-palette</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="caption grey--text">Color</v-list-item-title>
                                <v-list-item-subtitle class="subtitle-1">{{ car.color || 'No especificado'
                                  }}</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-col>

                        <!-- Columna derecha -->
                        <v-col cols="12" md="6">
                          <v-list dense class="transparent">
                            <v-list-item class="px-0">
                              <v-list-item-icon class="mr-2">
                                <v-icon>mdi-engine</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="caption grey--text">Condición</v-list-item-title>
                                <v-list-item-subtitle class="subtitle-1">
                                  {{ car.condition ? (car.condition === 'new' ? 'VN' : car.condition === 'taller' ? 'Taller' : 'VO') : 'No especificado' }}
                                </v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>

                            <v-list-item class="px-0">
                              <v-list-item-icon class="mr-2">
                                <v-icon>mdi-gas-station</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="caption grey--text">Combustible</v-list-item-title>
                                <v-list-item-subtitle class="subtitle-1">{{ car.fuel || 'No especificado'
                                  }}</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>

                            <v-list-item class="px-0">
                              <v-list-item-icon class="mr-2">
                                <v-icon>mdi-car-shift-pattern</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="caption grey--text">Transmisión</v-list-item-title>
                                <v-list-item-subtitle class="subtitle-1">{{ car.transmission || 'No especificada'
                                  }}</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>

                            <v-list-item class="px-0">
                              <v-list-item-icon class="mr-2">
                                <v-icon>mdi-identifier</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="caption grey--text">Bastidor</v-list-item-title>
                                <v-list-item-subtitle class="subtitle-1">{{ car.frame_number || 'No especificado'
                                  }}</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-tab-item>

                <!-- Pestaña de documentación (sin cambios) -->
                <v-tab-item>
                  <v-card flat>
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-list dense class="transparent">
                            <v-list-item class="px-0">
                              <v-list-item-icon class="mr-2">
                                <v-icon>mdi-card-bulleted-outline</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="caption grey--text">No. de Matrícula</v-list-item-title>
                                <v-list-item-subtitle class="subtitle-1">{{ car.plate_number || 'Sin matrícula'
                                  }}</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>

                            <v-list-item class="px-0">
                              <v-list-item-icon class="mr-2">
                                <v-icon>mdi-clipboard-text-outline</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="caption grey--text">No. de Pedido</v-list-item-title>
                                <v-list-item-subtitle class="subtitle-1">
                                  <span :class="{ 'font-weight-medium': car.order_number }">
                                    {{ car.order_number || 'No especificado' }}
                                  </span>
                                </v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-col>

                        <v-col cols="12" md="6">
                          <v-list dense class="transparent">
                            <v-list-item class="px-0">
                              <v-list-item-icon class="mr-2">
                                <v-icon>mdi-card-account-details-outline</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="caption grey--text">No. de IIC</v-list-item-title>
                                <v-list-item-subtitle class="subtitle-1">{{ car.iic_number || 'No especificado'
                                  }}</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>

                            <v-list-item class="px-0">
                              <v-list-item-icon class="mr-2">
                                <v-icon>mdi-file-document-check-outline</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="caption grey--text">Estado de
                                  documentación</v-list-item-title>
                                <v-list-item-subtitle class="subtitle-1">
                                  <v-chip x-small :color="car.plate_number ? 'success' : 'warning'" text-color="white"
                                    class="mr-1">
                                    {{ car.plate_number ? 'Matriculado' : 'Placas rojas' }}
                                  </v-chip>
                                </v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
              </v-tabs-items>
            </v-card>
          </v-col>

          <!-- Historial de ubicaciones -->
          <v-col cols="12" lg="7">
            <v-card class="mb-4 elevation-1">
              <v-card-title class="pb-1" :class="{ 'text-subtitle-1': $vuetify.breakpoint.smAndDown }">
                <v-icon left color="primary" :size="$vuetify.breakpoint.smAndDown ? 20 : 24">mdi-crosshairs-gps</v-icon>
                <span :class="{ 'text-body-2': $vuetify.breakpoint.smAndDown }">
                  Ubicación actual y historial
                </span>
              </v-card-title>
              <v-divider></v-divider>

              <v-card-text :class="{ 'pa-2': $vuetify.breakpoint.smAndDown, 'pt-4': $vuetify.breakpoint.mdAndUp }">
                <!-- Componente de historial de ubicaciones -->
                <vehiculo-ubicacion-history :fields="car" />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </template>

    <!-- Formulario de edición (sin cambios) -->
    <vehiculo-form v-if="visibleVehiculoForm" v-model="editItem" @save="updateDataTable"
      @cancel="visibleVehiculoForm = false"></vehiculo-form>

    <!-- Diálogo de confirmación de eliminación (sin cambios) -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="error--text">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          Eliminar vehículo
        </v-card-title>
        <v-card-text>
          ¿Está seguro que desea eliminar este vehículo? Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" @click="deleteVehiculo">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo de confirmación para Archivar vehículo -->
    <v-dialog v-model="freeVehicleDialog" max-width="400">
      <v-card>
        <v-card-title class="warning--text">
          <v-icon left color="warning">mdi-lock-open</v-icon>
          Archivar vehículo
        </v-card-title>
        <v-card-text>
          ¿Está seguro que desea archivar este vehículo?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="freeVehicleDialog = false">Cancelar</v-btn>
          <v-btn color="warning" @click="freeVehicle" :loading="freeVehicleLoading">Archivar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <transport-form
      v-if="selectedVehicleForTransport"
      :visible="transportFormVisible"
      :status="selectedVehicleForTransport"
      :is-assignment="isAssignmentMode"
      :is-movement="isMovementMode"
      :is-recepcionar="isRecepcionarMode"
      @close="closeTransportForm"
      @submit="handleTransportSubmit"
      @reload-with-movement="handleReloadWithMovement"
    />
  </div>
</template>

<script>
import VehiculoUbicacionHistory from './VehiculoUbicacionHistory.vue'
import VehiculoForm from './VehiculoForm.vue'
import VehiculoService from '../../services/VehiculoService'
import moment from 'moment'
import TransportForm from '../../components/TransportForm'
import { mapGetters } from 'vuex'
import errorHandler from '../../mixins/errorHandler'

export default {
  components: {
    VehiculoUbicacionHistory,
    VehiculoForm,
    TransportForm
  },

  mixins: [errorHandler],

  computed: {
    ...mapGetters(['HAS_PERMISSION']),

    canEditVehiculo () {
      // Permite editar si tiene permiso directo o como partner sobre el dealer del vehículo
      if (!this.car || !this.car.dealer) return false
      const dealerId = this.car.dealer.id
      const myDealerId = this.$store.getters.CURRENT_USER?.dealer?.id
      if (this.HAS_PERMISSION('editar_vehiculo')) return true
      // Permiso cruzado partner
      if (dealerId !== myDealerId && this.$store.getters.HAS_PARTNER_PERMISSION({ permission: 'editar_vehiculo', dealerId })) return true
      return false
    },

    canDeleteVehiculo () {
      if (!this.car || !this.car.dealer) return false
      const dealerId = this.car.dealer.id
      const myDealerId = this.$store.getters.CURRENT_USER?.dealer?.id
      if (this.HAS_PERMISSION('eliminar_vehiculo')) return true
      if (dealerId !== myDealerId && this.$store.getters.HAS_PARTNER_PERMISSION({ permission: 'eliminar_vehiculo', dealerId })) return true
      return false
    }
  },

  data () {
    return {
      loading: true,
      car: null,
      visibleVehiculoForm: false,
      editItem: null,
      detailsTab: 0,
      deleteDialog: false,
      freeVehicleDialog: false,
      freeVehicleLoading: false,
  transportFormVisible: false,
  selectedVehicleForTransport: null,
  isAssignmentMode: false,
  isMovementMode: false,
  isRecepcionarMode: false
    }
  },

  async created () {
    await this.loadCarDetails()
  },

  methods: {
    async loadCarDetails () {
      try {
        this.loading = true
        const carId = this.$route.params.id
        const { data } = await VehiculoService.view({ id: carId })
        this.car = data.data

        // Aquí puedes cargar los movimientos recientes del vehículo
        // this.loadRecentMovements(carId)
      } catch (error) {
        this.handleApiError(error, 'Error al cargar los detalles del vehículo')
      } finally {
        this.loading = false
      }
    },

    showVehiculoForm (item) {
      this.editItem = item
      this.visibleVehiculoForm = true
    },
    openAssignmentForm(car) {
      // Abre el TransportForm en modo cesión
      this.selectedVehicleForTransport = car
      this.isAssignmentMode = true
      this.isMovementMode = false
      this.transportFormVisible = true
    },

    updateDataTable () {
      this.visibleVehiculoForm = false
      this.loadCarDetails()
      this.$toast.success('Vehículo actualizado con éxito')
    },

    confirmDeleteVehiculo () {
      this.deleteDialog = true
    },

    deleteVehiculo () {
      this.deleteDialog = false
      // Implementa la eliminación real del vehículo
      VehiculoService.delete({ id: this.car.id })
        .then(() => {
          this.$toast.success('Vehículo eliminado con éxito')
          this.$router.push({ name: 'Vehiculo' })
        })
        .catch(error => {
          this.handleApiError(error, 'Error al eliminar el vehículo')
        })
    },

    addMovimiento () {
      // Implementa la navegación o el modal para crear un nuevo movimiento
      this.$router.push({
        name: 'MovimientoCreate',
        params: { vehiculo_id: this.car.id }
      })
    },

    /* getCurrentLocation () {
      return this.car.location ? this.car.location.name : 'No disponible'
    }, */

    getLastMovementDate () {
      return this.car.lastMovementDate ? moment(this.car.lastMovementDate).format('DD/MM/YYYY') : null
    },

    getCarIcon (condition) {
      return condition === 'new' ? 'mdi-car-estate' : 'mdi-car-hatchback'
    },

    getStatusColor (status) {
      const statusColorMap = {
        'unreported': 'red',
        'reported': 'orange',
        'transport': 'blue',
        'processing': 'purple',
        'completed': 'green'
      }
      return statusColorMap[status] || 'grey'
    },

    getStatusIcon (status) {
      const statusIconMap = {
        'unreported': 'mdi-alert-circle-outline',
        'reported': 'mdi-bell-ring-outline',
        'transport': 'mdi-truck-outline',
        'processing': 'mdi-progress-clock',
        'completed': 'mdi-check-circle-outline'
      }
      return statusIconMap[status] || 'mdi-help-circle-outline'
    },

    translateStatus (status) {
      const statusMap = {
        'unreported': 'No reportado',
        'reported': 'Reportado',
        'transport': 'Recepcionado',
        'processing': 'En proceso',
        'completed': 'Completado'
      }
      return statusMap[status] || status
    },
    openTransportForm (vehicle) {
      this.selectedVehicleForTransport = vehicle
      this.isAssignmentMode = false
      this.isMovementMode = true
      this.transportFormVisible = true
    },
    openTransportFormRecepcionar (vehicle) {
      this.selectedVehicleForTransport = vehicle
      this.isAssignmentMode = false
      this.isMovementMode = false
      this.isRecepcionarMode = true
      this.transportFormVisible = true
    },
    // Método para cerrar el formulario de transporte
    closeTransportForm () {
      this.transportFormVisible = false
      this.selectedVehicleForTransport = null
      this.isAssignmentMode = false
    },
    // Método para manejar el envío exitoso del formulario de transporte
    handleTransportSubmit (data) {
      this.transportFormVisible = false
      this.selectedVehicleForTransport = null
      this.isAssignmentMode = false
      // Recargar datos para reflejar el nuevo movimiento
      this.loadCarDetails()
      this.$toast.success('Petición de movimiento creada con éxito')
    },
    // Método para recargar el formulario con el nuevo movimiento de entrega
    handleReloadWithMovement (payload) {
      // Actualizar el vehículo seleccionado con los datos actualizados
      this.selectedVehicleForTransport = payload.vehicle
      
      // Mantener el formulario abierto pero resetear los modos
      // El formulario detectará automáticamente el nuevo movimiento en estado 'reported'
      this.isAssignmentMode = false
      this.isMovementMode = false
      this.isRecepcionarMode = false
    },
    // Método para verificar si se puede crear un movimiento
    canCreateMovement (vehicle) {
      if (!vehicle) return false
      
      // Si el vehículo está liberado (free), no se puede crear movimiento
      if (vehicle.status === 'free') return false
      
      if (vehicle.movements && vehicle.movements.length > 0) {
        // Ordenar movimientos por fecha (más recientes primero)
        const sortedMovements = [...vehicle.movements].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        )

        // Obtener el último movimiento
        const lastMovement = sortedMovements[0]
        if ((vehicle.status === 'registered' && lastMovement.type === 'entry') || (lastMovement.type === 'move' || lastMovement.type === 'assignment')) {
          return true
        }
        // Verificar si cumple con las condiciones
        return (lastMovement.type === 'exit' && lastMovement.status === 'completed') || (lastMovement !== null && lastMovement.type === 'assignment' && lastMovement.status === 'completed')
      }

      // Si no cumple ninguna condición, no mostrar el botón
      return false
    },
    // Método para verificar si se puede recepcionar un vehículo
    canRecepcionar (vehicle) {
      if (!vehicle) return false
      
      // Primero verificar si el estado del vehículo permite recepción
      const canRecepcionarByStatus = vehicle.status === 'created' || vehicle.status === 'free'
      
      // Si no cumple las condiciones de estado, no se puede recepcionar
      if (!canRecepcionarByStatus) return false
      
      // Si es colaborador, puede recepcionar sin restricción de dealer
      if (this.$store.getters.HAS_ROLE(['colaborator'])) return true
      
      // Verificar permisos
      const dealerId = vehicle.dealer?.id
      const myDealerId = this.$store.getters.CURRENT_USER?.dealer?.id
      
      // Permiso directo en su propio dealer
      if (dealerId === myDealerId) return true
      // Permiso como partner sobre el dealer del vehículo
      if (dealerId && dealerId !== myDealerId && this.$store.getters.IS_PARTNER) return true
      
      // Si no tiene permisos, no se puede recepcionar
      return false
    },
    canCesion (vehicle) {
      if (!vehicle || vehicle.status === 'free' || vehicle.movements.length === 0) return false
      if (vehicle.status !== 'registered') return false
      
      // Si es colaborador, puede crear cesiones sin restricción de dealer
      if (this.$store.getters.HAS_ROLE(['colaborator'])) return true
      
      // Permiso directo o partner sobre el dealer del vehículo
      const dealerId = vehicle.dealer?.id
      const myDealerId = this.$store.getters.CURRENT_USER?.dealer?.id

      if (this.HAS_PERMISSION('crear_cesion') && dealerId === myDealerId) return true

      if (dealerId && dealerId !== myDealerId && this.$store.getters.HAS_PARTNER_PERMISSION({ permission: 'crear_cesion', dealerId })) return true
      return false
    },
    canMov (vehicle) {
      if (!vehicle || vehicle.status === 'free' || vehicle.movements.length === 0) return false
      if (vehicle.status !== 'registered') return false
      
      // Si es colaborador, puede crear movimientos sin restricción de dealer
      if (this.$store.getters.HAS_ROLE(['colaborator'])) return true
      
      // Permiso directo o partner sobre el dealer del vehículo
      const dealerId = vehicle.dealer?.id
      const myDealerId = this.$store.getters.CURRENT_USER?.dealer?.id
      if (dealerId === myDealerId) return true
      if (dealerId && dealerId !== myDealerId && this.$store.getters.HAS_PARTNER_PERMISSION({ permission: 'crear_movimiento', dealerId })) return true
      return false
    },

    // Método para verificar si se puede liberar un vehículo
    canFreeVehicle (vehicle) {
      if (!vehicle) return false
      
      // No se puede liberar si ya está en estado 'free'
      if (vehicle.status === 'free') return false
      
      // Si no hay movimientos, no se puede liberar
      if (!vehicle.movements || vehicle.movements.length === 0) return false
      
      // Obtener el último movimiento
      const sortedMovements = [...vehicle.movements].sort(
        (a, b) => new Date(b.datetime) - new Date(a.datetime)
      )
      const lastMovement = sortedMovements[0]

      // Obtener el dealer del vehículo y el dealer del usuario actual
      const dealerId = vehicle.dealer?.id
      const myDealerId = this.$store.getters.CURRENT_USER?.dealer?.id

      // Primero verificar las condiciones del último movimiento
      const canFreeByMovementStatus = (lastMovement.type === 'exit' && lastMovement.status === 'completed') ||
             (lastMovement.type === 'assignment' && lastMovement.status === 'completed') || 
             (lastMovement.type === 'entry' && lastMovement.status === 'completed')
      
      // Si no cumple las condiciones del movimiento, no se puede liberar
      if (!canFreeByMovementStatus) return false

      // Si cumple las condiciones del movimiento, verificar permisos
      if (this.HAS_PERMISSION('editar_vehiculo') && dealerId === myDealerId) return true
      if (dealerId && dealerId !== myDealerId && this.$store.getters.HAS_PARTNER_PERMISSION({ permission: 'editar_vehiculo', dealerId })) return true

      // Si no tiene permisos, no se puede liberar
      return false
    },

    // Método para mostrar el diálogo de confirmación para Archivar vehículo
    confirmFreeVehicle () {
      this.freeVehicleDialog = true
    },

    // Método para liberar el vehículo
    async freeVehicle () {
      try {
        this.freeVehicleLoading = true
        
        const vehicleData = {
          id: this.car.id,
          status: 'free'
        }

        await VehiculoService.put(vehicleData)
        
        this.freeVehicleDialog = false
        this.$toast.success('Vehículo liberado con éxito')
        
        // Recargar los detalles del vehículo para reflejar el cambio de estado
        await this.loadCarDetails()
      } catch (error) {
        this.handleApiError(error, 'Error al liberar el vehículo')
      } finally {
        this.freeVehicleLoading = false
      }
    }
  }
}
</script>

<style scoped>
.vehicle-header {
  background-color: rgba(0, 0, 0, 0.01);
}

.info-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  background-color: rgba(0, 0, 0, 0.04);
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.7);
}

.detail-tabs .v-tab {
  letter-spacing: 0;
  min-width: 0;
  font-weight: 500;
}

.current-location {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background-color: rgba(0, 0, 0, 0.01);
  border-radius: 8px;
  border-left: 3px solid var(--v-primary-base);
}

.recent-movements .v-list-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.recent-movements .v-list-item:last-child {
  border-bottom: none;
}
</style>
